/**
 * GSplatGpuPicker - GPU 加速的高斯溅射拾取器
 *
 * 参考 SuperSplat 和 PlayCanvas Picker 实现，使用 GPU 渲染来获取 splat ID。
 *
 * 工作原理：
 * 1. 将每个 splat 的唯一 ID 编码为 RGBA 颜色
 * 2. 渲染到离屏 RenderTarget
 * 3. 读取指定像素的颜色并解码为 splat ID
 *
 * @remarks
 * 对于 SOGS GPU 资源（数据在 GPU 上），这是唯一可行的拾取方案。
 */

import * as THREE from 'three'
import { getGSplatBatchInstanceCount } from '@/plugins/gsplat/core/gsplatBatching'
import {
  readWebGLRenderTargetPixels,
  unbindWebGLPixelPackBuffer,
} from '@/plugins/gsplat/graphics/webglReadback'
import type { GSplatInfo } from '@/plugins/gsplat/lod/GSplatInfo'
import type { GSplatManager } from '@/plugins/gsplat/lod/GSplatManager'
import { GaussianSplatMaterial } from '@/plugins/gsplat/materials/GaussianSplatMaterial'
import type {
  IGSplatGeometry,
  IGSplatMaterial,
  IGSplatResource,
} from '@/plugins/gsplat/types/ResourceTypes'
import {
  DEFAULT_MAX_PICK_RESULTS,
  DEFAULT_PICK_ALPHA_CLIP,
  DEFAULT_PICK_BUFFER_SIZE,
} from '@/shared/constants'
import { getLoggerManager } from '@/shared/logging'

const log = getLoggerManager().getLogger('gsplat:spatial')

/**
 * GPU 拾取结果
 */
export interface GpuPickResult {
  /** Splat 索引（在当前 GSplatInfo 中的索引） */
  splatIndex: number
  /** 关联的 GSplatInfo */
  splatInfo: GSplatInfo
  /** 世界空间位置（从 centers 缓存获取） */
  worldPosition: THREE.Vector3
  /** 屏幕空间深度值（可选） */
  depth?: number
}

/**
 * GPU Picker 选项
 */
export interface GpuPickerOptions {
  /** 渲染目标宽度，默认使用屏幕宽度的 0.5 倍 */
  width?: number
  /** 渲染目标高度，默认使用屏幕高度的 0.5 倍 */
  height?: number
  /** Alpha 裁剪阈值，默认 0.3 */
  alphaClip?: number
}

/** 单次 GPU pick buffer 的最小深度状态。 */
export interface GpuPickDepthOptions {
  /** 生成当前拾取平面的原始世界射线。 */
  ray: THREE.Ray
  /** 从射线起点沿归一化方向计算的最小世界坐标距离。 */
  minDistance: number
}

/** GPU Picker 当前强引用资源的只读诊断快照。 */
export interface GSplatGpuPickerDiagnostics {
  /** 当前准备帧保留的 GSplatInfo 数量。 */
  readonly currentInfos: number
  /** 拾取材质缓存中的条目数量。 */
  readonly pickMaterials: number
}

interface WorkBufferOwnerRange {
  startWorkId: number
  endWorkId: number
  splatInfo: GSplatInfo
}

type PickIdRange =
  | {
      startId: number
      endId: number
      mode: 'direct'
      splatInfo: GSplatInfo
    }
  | {
      startId: number
      endId: number
      mode: 'workbuffer'
      ownerRanges: WorkBufferOwnerRange[]
    }

/**
 * GPU 加速的高斯溅射拾取器
 *
 * @example
 * ```typescript
 * // 创建 Picker
 * const picker = new GSplatGpuPicker(renderer, 512, 512)
 *
 * // 准备 pick buffer（在需要拾取前调用）
 * picker.prepare(camera, scene, gsplatMeshes)
 *
 * // 拾取
 * const result = picker.pick(mouseX, mouseY)
 * if (result) {
 *   console.log('选中 splat:', result.splatIndex)
 * }
 *
 * // 矩形选择
 * const results = picker.pickRect(x1, y1, x2, y2)
 * ```
 */
export class GSplatGpuPicker {
  /** WebGL 渲染器 */
  private renderer: THREE.WebGLRenderer

  /** 渲染目标宽度 */
  private width: number

  /** 渲染目标高度 */
  private height: number

  /** Alpha 裁剪阈值 */
  private alphaClip: number

  /** Pick 渲染目标 */
  private pickRenderTarget: THREE.WebGLRenderTarget

  /** 临时场景（用于 pick pass 渲染） */
  private pickScene: THREE.Scene

  /** ID -> GSplatInfo / WorkBuffer 区间映射 */
  private idRanges: PickIdRange[] = []

  /** 当前帧的 GSplatInfo 列表（供外部查询使用） */
  private currentSplatInfos: GSplatInfo[] = []

  /** Pick 材质缓存（直接 splatInfo / WorkBuffer sourceNode → Material） */
  private pickMaterials: Map<object, GaussianSplatMaterial> = new Map()

  /** 临时 Mesh 列表（用于渲染后清理） */
  private tempMeshes: THREE.Mesh[] = []
  private tempGeometries: THREE.BufferGeometry[] = []
  private tempCameraPosition = new THREE.Vector3()
  private tempSceneOrigin = new THREE.Vector3()

  /** 像素读取缓冲区 */
  private pixelBuffer: Uint8Array

  /** 是否已准备好 pick buffer */
  private prepared = false

  /** GPU渲染完成状态标记 */
  private renderCompleted = false

  /** prepare调用的帧号 */
  private prepareFrameId = -1
  private preparedCameraMatrixWorld = new THREE.Matrix4()
  private preparedCameraProjectionMatrix = new THREE.Matrix4()
  private hasPreparedCameraState = false
  private preparedMinDistance: number | undefined
  private preparedRayOrigin = new THREE.Vector3()
  private preparedRayDirection = new THREE.Vector3()

  /** 全局帧计数器（用于追踪GPU异步渲染状态） */
  private static currentFrameId = 0

  /**
   * 创建 GPU Picker
   *
   * 支持两种调用方式：
   * 1. `new GSplatGpuPicker(renderer, width, height, options)`
   * 2. `new GSplatGpuPicker(renderer, options)`
   *
   * @param renderer - WebGL 渲染器
   * @param widthOrOptions - 渲染目标宽度或选项对象
   * @param height - 渲染目标高度（当第二个参数是宽度时使用）
   * @param options - 可选配置（当第二、三个参数是宽高时使用）
   */
  constructor(
    renderer: THREE.WebGLRenderer,
    widthOrOptions?: number | GpuPickerOptions,
    height?: number,
    options?: GpuPickerOptions
  ) {
    this.renderer = renderer

    // 判断调用方式
    if (typeof widthOrOptions === 'object') {
      // 方式 2: new GSplatGpuPicker(renderer, options)
      const opts = widthOrOptions as GpuPickerOptions
      this.width = opts.width ?? DEFAULT_PICK_BUFFER_SIZE
      this.height = opts.height ?? DEFAULT_PICK_BUFFER_SIZE
      this.alphaClip = opts.alphaClip ?? DEFAULT_PICK_ALPHA_CLIP
    } else {
      // 方式 1: new GSplatGpuPicker(renderer, width, height, options)
      this.width = widthOrOptions ?? options?.width ?? DEFAULT_PICK_BUFFER_SIZE
      this.height = height ?? options?.height ?? DEFAULT_PICK_BUFFER_SIZE
      this.alphaClip = options?.alphaClip ?? DEFAULT_PICK_ALPHA_CLIP
    }

    // 创建 pick 渲染目标
    this.pickRenderTarget = new THREE.WebGLRenderTarget(this.width, this.height, {
      format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      generateMipmaps: false,
      stencilBuffer: false,
      depthBuffer: true,
    })

    // 验证创建结果
    if (this.width <= 0 || this.height <= 0) {
      log.error(
        '[GSplatGpuPicker] Invalid render target size: width=' +
          this.width +
          ', height=' +
          this.height
      )
    }

    // 创建临时场景
    this.pickScene = new THREE.Scene()

    // 预分配像素缓冲区
    this.pixelBuffer = new Uint8Array(4)
  }

  /**
   * 调整渲染目标大小
   *
   * @param width - 新宽度
   * @param height - 新高度
   */
  resize(width: number, height: number): void {
    if (this.width === width && this.height === height) return

    this.width = width
    this.height = height
    this.pickRenderTarget.setSize(width, height)
    this.prepared = false
    this.renderCompleted = false
    this.hasPreparedCameraState = false
  }

  /**
   * 准备 Pick Buffer。
   *
   * 遍历所有 GSplatInfo，为每个 splat 分配唯一 ID 并渲染到 pick buffer。
   *
   * @param camera - 相机
   * @param manager - 目标 GSplatManager，用于提取 WorkBuffer
   * @param splatInfos - 要拾取的 GSplatInfo 列表
   */
  prepare(
    camera: THREE.Camera,
    manager: GSplatManager,
    splatInfos: GSplatInfo[],
    pickDepth?: GpuPickDepthOptions
  ): void {
    // 清空映射
    this.idRanges.length = 0
    this.currentSplatInfos = splatInfos

    // 记录prepare的帧号，重置就绪状态
    this.prepareFrameId = GSplatGpuPicker.currentFrameId
    this.renderCompleted = false

    // 清空临时场景
    this.clearPickScene()
    camera.updateMatrixWorld(true)
    camera.getWorldPosition(this.tempCameraPosition)
    this.preparedCameraMatrixWorld.copy(camera.matrixWorld)
    this.preparedCameraProjectionMatrix.copy(camera.projectionMatrix)
    this.hasPreparedCameraState = true
    this.preparedMinDistance = pickDepth?.minDistance
    if (pickDepth) {
      this.preparedRayOrigin.copy(pickDepth.ray.origin)
      this.preparedRayDirection.copy(pickDepth.ray.direction).normalize()
    } else {
      this.preparedRayOrigin.set(0, 0, 0)
      this.preparedRayDirection.set(0, 0, 0)
    }
    const workBufferGroups = new Map<
      object,
      {
        sourceNode: THREE.Object3D & {
          geometry?: unknown
          material?: unknown
          children?: THREE.Object3D[]
        }
        members: GSplatInfo[]
      }
    >()

    let globalId = 0
    for (const splatInfo of splatInfos) {
      const sourceNode = this.resolvePickSourceNode(splatInfo)
      if (sourceNode && this.isWorkBufferPickSource(sourceNode)) {
        const key = sourceNode as object
        let group = workBufferGroups.get(key)
        if (!group) {
          group = { sourceNode, members: [] }
          workBufferGroups.set(key, group)
        }
        group.members.push(splatInfo)
        continue
      }

      const startId = globalId
      globalId += splatInfo.numSplats
      this.idRanges.push({
        startId,
        endId: globalId,
        mode: 'direct',
        splatInfo,
      })

      const materialKey = this.getPickMaterialKey(splatInfo, sourceNode)
      let pickMaterial = this.pickMaterials.get(materialKey)
      if (!pickMaterial) {
        const newMaterial = this.createPickMaterial(splatInfo)
        if (!newMaterial) {
          continue
        }
        pickMaterial = newMaterial
        this.pickMaterials.set(materialKey, pickMaterial)
      }

      this.syncClipState(pickMaterial, sourceNode?.material ?? splatInfo.material)
      this.syncScalarState(pickMaterial, manager)
      pickMaterial.setPickId(this.encodePickId(startId))
      pickMaterial.setPickMinDistance(camera, pickDepth?.ray, pickDepth?.minDistance)

      const transformNode = (sourceNode ?? splatInfo.node) as unknown as THREE.Object3D
      transformNode.updateMatrixWorld(true)
      pickMaterial.setCameraParams(camera)
      pickMaterial.setViewport(this.width, this.height)
      pickMaterial.setCameraPositionWorld(this.tempCameraPosition)
      pickMaterial.setOriginToCamera(
        this.resolveSceneOrigin(transformNode, splatInfo.node as unknown as THREE.Object3D),
        this.tempCameraPosition
      )

      const geometry =
        (sourceNode?.geometry as THREE.BufferGeometry | undefined) ??
        (splatInfo.geometry as unknown as THREE.BufferGeometry | undefined)
      if (!geometry) {
        continue
      }

      const pickMesh = new THREE.Mesh(geometry as THREE.BufferGeometry, pickMaterial)
      pickMesh.matrixWorld.copy(transformNode.matrixWorld)
      pickMesh.matrixAutoUpdate = false
      pickMesh.matrixWorldNeedsUpdate = true
      pickMesh.frustumCulled = false
      this.pickScene.add(pickMesh)
      this.tempMeshes.push(pickMesh)
    }

    for (const [, group] of workBufferGroups) {
      const sourceNode = group.sourceNode
      const geometry = sourceNode.geometry as THREE.BufferGeometry | undefined
      if (!geometry || group.members.length === 0) {
        continue
      }

      const visibleSplatCount = this.getWorkBufferVisibleSplatCount(sourceNode, manager)
      const batchInstanceCount = getGSplatBatchInstanceCount(visibleSplatCount)
      if (visibleSplatCount <= 0 || batchInstanceCount <= 0) {
        continue
      }

      const startId = globalId
      globalId += visibleSplatCount
      this.idRanges.push({
        startId,
        endId: globalId,
        mode: 'workbuffer',
        ownerRanges: group.members
          .map((splatInfo) => ({
            startWorkId: splatInfo.lineStart * manager.textureSize,
            endWorkId: splatInfo.lineStart * manager.textureSize + splatInfo.activeSplats,
            splatInfo,
          }))
          .sort((a, b) => a.startWorkId - b.startWorkId),
      })

      const materialKey = sourceNode as object
      let pickMaterial = this.pickMaterials.get(materialKey)
      if (!pickMaterial) {
        const newMaterial = this.createPickMaterial(group.members[0])
        if (!newMaterial) {
          continue
        }
        pickMaterial = newMaterial
        this.pickMaterials.set(materialKey, pickMaterial)
      }

      this.syncClipState(pickMaterial, sourceNode.material)
      this.syncScalarState(pickMaterial, manager)
      pickMaterial.setUnifiedLODTextures(
        manager.colorTexture,
        manager.splatTexture0,
        manager.splatTexture1,
        manager.orderTexture,
        manager.textureSize
      )
      pickMaterial.setPickId(this.encodePickId(startId))
      pickMaterial.setPickMinDistance(camera, pickDepth?.ray, pickDepth?.minDistance)
      pickMaterial.setMetadata(visibleSplatCount, manager.textureSize, manager.textureSize)
      sourceNode.updateMatrixWorld(true)
      pickMaterial.setCameraParams(camera)
      pickMaterial.setViewport(this.width, this.height)
      pickMaterial.setCameraPositionWorld(this.tempCameraPosition)
      pickMaterial.setOriginToCamera(
        this.resolveSceneOrigin(sourceNode, group.members[0].node as unknown as THREE.Object3D),
        this.tempCameraPosition
      )

      const pickGeometry = geometry.clone() as THREE.BufferGeometry
      if ('instanceCount' in pickGeometry) {
        ;(pickGeometry as THREE.InstancedBufferGeometry).instanceCount = batchInstanceCount
      }
      this.tempGeometries.push(pickGeometry)

      const pickMesh = new THREE.Mesh(pickGeometry, pickMaterial)
      pickMesh.matrixWorld.copy(sourceNode.matrixWorld)
      pickMesh.matrixAutoUpdate = false
      pickMesh.matrixWorldNeedsUpdate = true
      pickMesh.frustumCulled = false
      this.pickScene.add(pickMesh)
      this.tempMeshes.push(pickMesh)
    }

    // 渲染到 pick buffer
    this.renderPickBuffer(camera)

    this.prepared = true
  }

  /**
   * 检查GPU Picker是否真正就绪
   *
   * 由于GPU渲染是异步的，prepare()调用后需要等待数帧才能确保GPU渲染完成。
   * 过早调用pick()会导致readPixels阻塞等待GPU，造成卡顿。
   *
   * 策略：
   * 1. prepare后至少等待2帧（GPU命令提交和执行需要时间）
   * 2. 成功拾取后标记为完成状态
   * 3. 超过5帧仍未成功，也认为可以尝试（兜底策略）
   *
   * @returns 是否就绪可以执行拾取
   */
  isReady(): boolean {
    if (!this.prepared) {
      return false
    }

    // 策略1：帧数延迟（至少等待2帧）
    const framesSincePrepare = GSplatGpuPicker.currentFrameId - this.prepareFrameId
    if (framesSincePrepare < 2) {
      return false
    }

    // 策略2：成功拾取后标记为完成
    if (this.renderCompleted) {
      return true
    }

    // 策略3：超过5帧仍未成功，也认为可以尝试（兜底）
    return framesSincePrepare >= 5
  }

  /**
   * 检查当前 pick buffer 是否对应指定相机状态。
   *
   * @param camera - 要比较的相机
   * @returns pick buffer 是否仍适用于该相机
   */
  isPreparedFor(camera: THREE.Camera, ray?: THREE.Ray, minDistance?: number): boolean {
    if (!this.prepared || !this.hasPreparedCameraState) {
      return false
    }

    camera.updateMatrixWorld(true)
    if (this.preparedMinDistance !== minDistance) {
      return false
    }
    if (
      minDistance !== undefined &&
      (!ray ||
        !this.preparedRayOrigin.equals(ray.origin) ||
        !this.preparedRayDirection.equals(ray.direction.clone().normalize()))
    ) {
      return false
    }

    return (
      this.preparedCameraMatrixWorld.equals(camera.matrixWorld) &&
      this.preparedCameraProjectionMatrix.equals(camera.projectionMatrix)
    )
  }

  /**
   * 为 GSplatInfo 创建 Pick 材质
   */
  private createPickMaterial(splatInfo: GSplatInfo): GaussianSplatMaterial | null {
    const sourceNode = this.resolvePickSourceNode(splatInfo)
    const geometry = (sourceNode?.geometry ?? splatInfo.geometry) as IGSplatGeometry | undefined
    if (!geometry) {
      return null
    }

    // 创建 Pick Pass 材质
    const pickMaterial = new GaussianSplatMaterial({
      pass: 'pick',
      alphaClip: this.alphaClip,
      enableSH: false, // Pick Pass 不需要 SH
      depthWrite: true,
      depthTest: true,
    })

    // 绑定几何数据纹理
    // 注意：geometry 可能来自 WorkBuffer 或独立纹理
    if (
      geometry.dataTextureA &&
      geometry.dataTextureB &&
      geometry.colorTexture &&
      geometry.orderTexture
    ) {
      // 标准纹理模式：使用接口类型访问几何体纹理
      pickMaterial.setDataTextures(
        geometry.dataTextureA,
        geometry.dataTextureB,
        geometry.colorTexture,
        geometry.orderTexture
      )
      pickMaterial.setMetadata(splatInfo.numSplats, geometry.dataWidth, geometry.orderWidth)
    } else {
      // 尝试从源材质的 uniforms 获取
      const srcMaterial =
        (sourceNode?.material as IGSplatMaterial | undefined) ??
        (splatInfo.material as IGSplatMaterial | undefined)
      if (srcMaterial?.uniforms) {
        this.copyMaterialUniforms(pickMaterial, srcMaterial)
      }
    }

    this.syncClipState(pickMaterial, sourceNode?.material ?? splatInfo.material)
    return pickMaterial
  }

  private resolvePickSourceNode(
    splatInfo: GSplatInfo
  ):
    | (THREE.Object3D & { geometry?: unknown; material?: unknown; children?: THREE.Object3D[] })
    | null {
    const node = splatInfo.node as THREE.Object3D & {
      geometry?: unknown
      material?: unknown
      children?: THREE.Object3D[]
    }

    if (this.isPickRenderableNode(node)) {
      return node
    }

    return this.findPickRenderableDescendant(node)
  }

  private resolveSceneOrigin(
    sourceNode: THREE.Object3D,
    fallbackNode: THREE.Object3D
  ): THREE.Vector3 {
    const sourceOverride = (sourceNode as { __sceneOriginOverride?: THREE.Vector3 })
      .__sceneOriginOverride
    if (sourceOverride) {
      return this.tempSceneOrigin.copy(sourceOverride)
    }

    const fallbackOverride = (fallbackNode as { __sceneOriginOverride?: THREE.Vector3 })
      .__sceneOriginOverride
    if (fallbackOverride) {
      return this.tempSceneOrigin.copy(fallbackOverride)
    }

    return this.tempSceneOrigin.setFromMatrixPosition(sourceNode.matrixWorld)
  }

  private findPickRenderableDescendant(
    node: THREE.Object3D & { geometry?: unknown; material?: unknown; children?: THREE.Object3D[] }
  ):
    | (THREE.Object3D & { geometry?: unknown; material?: unknown; children?: THREE.Object3D[] })
    | null {
    const children = Array.isArray(node.children) ? node.children : []

    for (const child of children) {
      const candidate = child as THREE.Object3D & {
        geometry?: unknown
        material?: unknown
        children?: THREE.Object3D[]
      }

      if (this.isPickRenderableNode(candidate)) {
        return candidate
      }

      const descendant = this.findPickRenderableDescendant(candidate)
      if (descendant) {
        return descendant
      }
    }

    return null
  }

  private isPickRenderableNode(
    node: { geometry?: unknown; material?: unknown } | null | undefined
  ): node is THREE.Object3D & { geometry: IGSplatGeometry; material: IGSplatMaterial } {
    if (!node?.geometry || !node?.material) {
      return false
    }

    const material = node.material as IGSplatMaterial
    return Boolean(material.uniforms?.transformA) && Boolean(material.uniforms?.splatOrder)
  }

  /**
   * 复制源材质的 uniforms 到目标材质
   *
   * 这里只同步 pick 必需的数据纹理、标量过滤和 WorkBuffer defines。
   * 最终渲染 shader effect 属于视觉 pass，pick pass 默认不继承。
   */
  private copyMaterialUniforms(target: IGSplatMaterial, source: IGSplatMaterial): void {
    // 基础 uniforms
    const uniformNames = [
      'transformA',
      'transformB',
      'splatColor',
      'splatOrder',
      'numSplats',
      'dataWidth',
      'orderWidth',
      'splatScalar',
      'scalarFilterEnabled',
      'scalarFilterRange',
    ]

    for (const name of uniformNames) {
      if (source.uniforms[name]) {
        target.uniforms[name] = target.uniforms[name] || {}
        target.uniforms[name].value = source.uniforms[name].value
      }
    }

    // 复制必要的 defines 和对应的纹理
    if (source.defines.GSPLAT_SOGS_DATA) {
      target.defines.GSPLAT_SOGS_DATA = 1
      // 复制 SOGS 纹理 uniforms
      const sogsUniforms = ['splatMeansLow', 'splatMeansHigh', 'splatQuats', 'splatScales']

      for (const name of sogsUniforms) {
        if (source.uniforms[name]) {
          target.uniforms[name] = target.uniforms[name] || {}
          target.uniforms[name].value = source.uniforms[name].value
        }
      }

      // 复制 SOGS 范围参数（Vector3 需要使用 copy）
      if (source.uniforms.sogsMeans_mins && target.uniforms.sogsMeans_mins) {
        target.uniforms.sogsMeans_mins.value.copy(source.uniforms.sogsMeans_mins.value)
      }
      if (source.uniforms.sogsMeans_maxs && target.uniforms.sogsMeans_maxs) {
        target.uniforms.sogsMeans_maxs.value.copy(source.uniforms.sogsMeans_maxs.value)
      }
    }

    if (source.defines.GSPLAT_SOGS_V2) {
      target.defines.GSPLAT_SOGS_V2 = 1
    }

    if (source.defines.GSPLAT_WORKBUFFER_DATA) {
      target.defines.GSPLAT_WORKBUFFER_DATA = 1
    }

    this.syncMaterialDefine(target, 'HAS_SCALAR_FILTER', Boolean(source.defines.HAS_SCALAR_FILTER))
  }

  private syncMaterialDefine(
    material: IGSplatMaterial,
    defineName: string,
    enabled: boolean
  ): void {
    const hadDefine = defineName in material.defines
    if (enabled === hadDefine) {
      return
    }

    if (enabled) {
      material.defines[defineName] = 1
    } else {
      delete material.defines[defineName]
    }
    material.needsUpdate = true
  }

  private syncScalarState(material: GaussianSplatMaterial, manager: GSplatManager): void {
    const filterConfig = manager.getScalarFilterConfig()
    if (filterConfig?.enabled && manager.scalarTexture && manager.scalarTextureVersion > 0) {
      material.setScalarTexture(manager.scalarTexture)
      material.setScalarFilter(true, filterConfig.min, filterConfig.max)
      return
    }

    material.setScalarTexture(null)
    material.setScalarFilter(false, 0, 1)
  }

  /**
   * Pick material 只借用 unified render material 持有的裁切纹理。
   * 每次 prepare 都同步，避免缓存材质继续使用已替换或已清空的状态。
   */
  private syncClipState(material: GaussianSplatMaterial, source: unknown): void {
    if (source instanceof GaussianSplatMaterial) {
      material.copyClipStateFrom(source)
      return
    }

    material.clearClipPacket()
  }

  private encodePickId(startId: number): [number, number, number, number] {
    return [
      (startId & 0xff) / 255,
      ((startId >> 8) & 0xff) / 255,
      ((startId >> 16) & 0xff) / 255,
      ((startId >> 24) & 0xff) / 255,
    ]
  }

  private getPickMaterialKey(
    splatInfo: GSplatInfo,
    sourceNode:
      | (THREE.Object3D & { geometry?: unknown; material?: unknown; children?: THREE.Object3D[] })
      | null
  ): object {
    return sourceNode && this.isWorkBufferPickSource(sourceNode) ? sourceNode : splatInfo
  }

  private isWorkBufferPickSource(
    sourceNode: THREE.Object3D & {
      geometry?: unknown
      material?: unknown
      children?: THREE.Object3D[]
    }
  ): boolean {
    const material = sourceNode.material as IGSplatMaterial | undefined
    return Boolean(material?.defines?.GSPLAT_WORKBUFFER_DATA)
  }

  private getWorkBufferVisibleSplatCount(
    sourceNode: THREE.Object3D & {
      geometry?: unknown
      material?: unknown
      children?: THREE.Object3D[]
    },
    manager: GSplatManager
  ): number {
    const material = sourceNode.material as IGSplatMaterial | undefined
    const materialCount = material?.uniforms?.numSplats?.value
    if (typeof materialCount === 'number' && Number.isFinite(materialCount) && materialCount >= 0) {
      return Math.floor(materialCount)
    }

    return Math.max(0, Math.floor(manager.visibleSplatCount))
  }

  /**
   * 清空 Pick 场景
   */
  /** 二分查找 globalId 所属的 splatInfo 区间 */
  private findRangeByGlobalId(globalId: number): PickIdRange | null {
    const ranges = this.idRanges
    let lo = 0
    let hi = ranges.length - 1
    while (lo <= hi) {
      const mid = (lo + hi) >>> 1
      const r = ranges[mid]
      if (globalId < r.startId) {
        hi = mid - 1
      } else if (globalId >= r.endId) {
        lo = mid + 1
      } else {
        return r
      }
    }
    return null
  }

  private findWorkBufferOwnerById(
    ownerRanges: WorkBufferOwnerRange[],
    workBufferId: number
  ): WorkBufferOwnerRange | null {
    let lo = 0
    let hi = ownerRanges.length - 1
    while (lo <= hi) {
      const mid = (lo + hi) >>> 1
      const range = ownerRanges[mid]
      if (workBufferId < range.startWorkId) {
        hi = mid - 1
      } else if (workBufferId >= range.endWorkId) {
        lo = mid + 1
      } else {
        return range
      }
    }
    return null
  }

  private getOriginalIndexFromActiveIndex(splatInfo: GSplatInfo, activeIndex: number): number {
    if (activeIndex < 0) {
      return -1
    }

    if (splatInfo.intervals.length === 0) {
      return activeIndex < splatInfo.activeSplats ? activeIndex : -1
    }

    let cursor = 0
    for (let i = 0; i < splatInfo.intervals.length; i += 2) {
      const start = splatInfo.intervals[i]
      const end = splatInfo.intervals[i + 1]
      const segmentLength = end - start
      if (activeIndex < cursor + segmentLength) {
        return start + (activeIndex - cursor)
      }
      cursor += segmentLength
    }

    return -1
  }

  private clearPickScene(): void {
    // 从场景中移除所有临时 Mesh
    for (const mesh of this.tempMeshes) {
      this.pickScene.remove(mesh)
    }
    this.tempMeshes = []
    for (const geometry of this.tempGeometries) {
      geometry.dispose()
    }
    this.tempGeometries = []
  }

  /**
   * 渲染 Pick Buffer
   */
  private renderPickBuffer(camera: THREE.Camera): void {
    // 保存当前渲染状态
    const currentRenderTarget = this.renderer.getRenderTarget()
    const currentClearColor = new THREE.Color()
    this.renderer.getClearColor(currentClearColor)
    const currentClearAlpha = this.renderer.getClearAlpha()
    const currentViewport =
      typeof this.renderer.getViewport === 'function'
        ? this.renderer.getViewport(new THREE.Vector4())
        : null
    const currentScissor =
      typeof this.renderer.getScissor === 'function'
        ? this.renderer.getScissor(new THREE.Vector4())
        : null
    const currentScissorTest =
      typeof this.renderer.getScissorTest === 'function' ? this.renderer.getScissorTest() : null

    // 设置 pick 渲染目标
    this.pickRenderTarget.viewport.set(0, 0, this.width, this.height)
    this.pickRenderTarget.scissor.set(0, 0, this.width, this.height)
    this.pickRenderTarget.scissorTest = false
    this.renderer.setRenderTarget(this.pickRenderTarget)
    this.renderer.setClearColor(0xffffffff, 1) // 白色 (0xFFFFFFFF) 表示无选择
    this.renderer.clear(true, true, false)

    // 渲染 pick scene
    this.renderer.render(this.pickScene, camera)

    // 恢复渲染状态
    this.renderer.setRenderTarget(currentRenderTarget)
    if (currentViewport && typeof this.renderer.setViewport === 'function') {
      this.renderer.setViewport(
        currentViewport.x,
        currentViewport.y,
        currentViewport.z,
        currentViewport.w
      )
    }
    if (currentScissor && typeof this.renderer.setScissor === 'function') {
      this.renderer.setScissor(
        currentScissor.x,
        currentScissor.y,
        currentScissor.z,
        currentScissor.w
      )
    }
    if (currentScissorTest !== null && typeof this.renderer.setScissorTest === 'function') {
      this.renderer.setScissorTest(currentScissorTest)
    }
    this.renderer.setClearColor(currentClearColor, currentClearAlpha)
  }

  /**
   * 拾取单个像素
   *
   * @param screenX - 屏幕 X 坐标（像素）
   * @param screenY - 屏幕 Y 坐标（像素）
   * @param screenWidth - 屏幕宽度
   * @param screenHeight - 屏幕高度
   * @returns 拾取结果或 null
   */
  pick(
    screenX: number,
    screenY: number,
    screenWidth: number,
    screenHeight: number
  ): GpuPickResult | null {
    const results = this.pickRect(screenX, screenY, 1, 1, screenWidth, screenHeight)
    return results.length > 0 ? results[0] : null
  }

  /**
   * 拾取矩形区域
   *
   * @param x - 矩形左上角 X（屏幕像素）
   * @param y - 矩形左上角 Y（屏幕像素）
   * @param width - 矩形宽度
   * @param height - 矩形高度
   * @param screenWidth - 屏幕宽度
   * @param screenHeight - 屏幕高度
   * @returns 拾取结果数组
   */
  pickRect(
    x: number,
    y: number,
    width: number,
    height: number,
    screenWidth: number,
    screenHeight: number
  ): GpuPickResult[] {
    if (!this.prepared) {
      log.warn('[GSplatGpuPicker] 未准备 pick buffer，请先调用 prepare()')
      return []
    }

    // 验证 RenderTarget 尺寸
    if (this.width <= 0 || this.height <= 0) {
      log.error(`[GSplatGpuPicker] Invalid render target size: ${this.width}x${this.height}`)
      return []
    }

    // 将屏幕坐标转换为 pick buffer 坐标
    const scaleX = this.width / screenWidth
    const scaleY = this.height / screenHeight

    const bufferX = Math.floor(x * scaleX)
    const bufferY = Math.floor(y * scaleY)
    const bufferWidth = Math.max(1, Math.floor(width * scaleX))
    const bufferHeight = Math.max(1, Math.floor(height * scaleY))

    // 边界检查
    if (
      bufferX < 0 ||
      bufferY < 0 ||
      bufferX >= this.width ||
      bufferY >= this.height ||
      bufferWidth <= 0 ||
      bufferHeight <= 0
    ) {
      log.warn(
        '[GSplatGpuPicker] Pick coordinates out of bounds: bufferX=' +
          bufferX +
          ', bufferY=' +
          bufferY +
          ', bufferWidth=' +
          bufferWidth +
          ', bufferHeight=' +
          bufferHeight +
          ', rtWidth=' +
          this.width +
          ', rtHeight=' +
          this.height
      )
      return []
    }

    // 确保像素缓冲区足够大
    const pixelCount = bufferWidth * bufferHeight * 4
    if (this.pixelBuffer.length < pixelCount) {
      this.pixelBuffer = new Uint8Array(pixelCount)
    }

    // 读取像素（注意 WebGL Y 轴翻转）
    const gl = this.renderer.getContext()
    const currentRenderTarget = this.renderer.getRenderTarget()

    try {
      this.renderer.setRenderTarget(this.pickRenderTarget)

      // 验证 Framebuffer 状态
      const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER)
      if (status !== gl.FRAMEBUFFER_COMPLETE) {
        log.error(`[GSplatGpuPicker] Framebuffer is incomplete: ${status}`)
        this.renderer.setRenderTarget(currentRenderTarget)
        return []
      }

      unbindWebGLPixelPackBuffer(this.renderer)
      gl.readPixels(
        bufferX,
        this.height - bufferY - bufferHeight,
        bufferWidth,
        bufferHeight,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        this.pixelBuffer
      )
    } catch (error) {
      log.error(
        '[GSplatGpuPicker] Error reading pixels: ' +
          (error instanceof Error ? error.message : String(error))
      )
      return []
    } finally {
      this.renderer.setRenderTarget(currentRenderTarget)
    }

    // 解码像素
    const results = this.decodePixels(this.pixelBuffer, bufferWidth * bufferHeight)

    // 成功读取像素，标记渲染已完成（用于isReady()检测）
    if (results.length > 0) {
      this.renderCompleted = true
    }

    return results
  }

  /**
   * 解码像素数据为拾取结果
   *
   * @param pixels - 像素数据
   * @param count - 像素数量
   * @returns 拾取结果数组
   */
  private decodePixels(pixels: Uint8Array, count: number): GpuPickResult[] {
    const results: GpuPickResult[] = []
    const seen = new Set<number>()

    for (let i = 0; i < count; i++) {
      const offset = i * 4
      const r = pixels[offset + 0]
      const g = pixels[offset + 1]
      const b = pixels[offset + 2]
      const a = pixels[offset + 3]

      // 解码 ID（与 SuperSplat 一致的编码方式）
      const globalId = (r | (g << 8) | (b << 16) | (a << 24)) >>> 0

      // 跳过白色（无选择）和重复
      if (globalId === 0xffffffff || seen.has(globalId)) {
        continue
      }
      seen.add(globalId)

      // 二分查找对应的 GSplatInfo（O(log N) 替代 O(1) Map，但避免了百万级 Map 构建）
      const range = this.findRangeByGlobalId(globalId)
      if (!range) {
        continue
      }

      let splatInfo: GSplatInfo | null = null
      let originalIndex = -1

      if (range.mode === 'direct') {
        splatInfo = range.splatInfo
        originalIndex = globalId - range.startId
      } else {
        const workBufferId = globalId - range.startId
        const ownerRange = this.findWorkBufferOwnerById(range.ownerRanges, workBufferId)
        if (!ownerRange) {
          continue
        }

        splatInfo = ownerRange.splatInfo
        originalIndex = this.getOriginalIndexFromActiveIndex(
          ownerRange.splatInfo,
          workBufferId - ownerRange.startWorkId
        )
      }

      if (!splatInfo || originalIndex < 0) {
        continue
      }

      const worldPosition = new THREE.Vector3()
      try {
        splatInfo.getSplatWorldCenter(originalIndex, worldPosition)
      } catch (_e) {
        worldPosition.set(0, 0, 0)
      }

      results.push({
        splatIndex: originalIndex,
        splatInfo,
        worldPosition,
      })
    }

    return results
  }

  /**
   * 异步拾取（支持 WebGPU，减少 GPU 阻塞）
   *
   * @param screenX - 屏幕 X 坐标
   * @param screenY - 屏幕 Y 坐标
   * @param screenWidth - 屏幕宽度
   * @param screenHeight - 屏幕高度
   * @returns Promise<拾取结果或 null>
   */
  async pickAsync(
    screenX: number,
    screenY: number,
    screenWidth: number,
    screenHeight: number
  ): Promise<GpuPickResult | null> {
    const results = await this.pickRectAsync(screenX, screenY, 1, 1, screenWidth, screenHeight)
    return results.length > 0 ? results[0] : null
  }

  /**
   * 异步矩形拾取
   *
   * @param x - 矩形左上角 X
   * @param y - 矩形左上角 Y
   * @param width - 矩形宽度
   * @param height - 矩形高度
   * @param screenWidth - 屏幕宽度
   * @param screenHeight - 屏幕高度
   * @returns Promise<拾取结果数组>
   */
  async pickRectAsync(
    x: number,
    y: number,
    width: number,
    height: number,
    screenWidth: number,
    screenHeight: number
  ): Promise<GpuPickResult[]> {
    if (!this.prepared) {
      log.warn('[GSplatGpuPicker] 未准备 pick buffer，请先调用 prepare()')
      return []
    }

    // 将屏幕坐标转换为 pick buffer 坐标
    const scaleX = this.width / screenWidth
    const scaleY = this.height / screenHeight

    const bufferX = Math.floor(x * scaleX)
    const bufferY = Math.floor(y * scaleY)
    const bufferWidth = Math.max(1, Math.floor(width * scaleX))
    const bufferHeight = Math.max(1, Math.floor(height * scaleY))

    const pixels = new Uint8Array(bufferWidth * bufferHeight * 4)
    await readWebGLRenderTargetPixels(
      this.renderer,
      this.pickRenderTarget,
      bufferX,
      this.height - bufferY - bufferHeight,
      bufferWidth,
      bufferHeight,
      pixels
    )
    return this.decodePixels(pixels, bufferWidth * bufferHeight)
  }

  /**
   * 使用简化方案进行拾取（基于 centers 数据）
   *
   * 对于 SOGS GPU 资源，由于没有 scale/rotation 的 CPU 数据，
   * 我们使用简化的球体近似进行拾取：
   * 1. 从 resource.centers 获取所有 splat 的中心点
   * 2. 将光线与每个中心点的小球体进行相交测试
   *
   * @param ray - 世界空间光线
   * @param splatInfos - GSplatInfo 列表
   * @param sphereRadius - 球体半径（用于近似），默认 0.01
   * @param maxResults - 最大返回结果数
   * @returns 拾取结果数组
   */
  static pickWithCenters(
    ray: THREE.Ray,
    splatInfos: GSplatInfo[],
    sphereRadius: number = 0.01,
    maxResults: number = DEFAULT_MAX_PICK_RESULTS,
    accept?: (splatInfo: GSplatInfo, splatIndex: number, worldPosition: THREE.Vector3) => boolean
  ): GpuPickResult[] {
    const tempCenter = new THREE.Vector3()
    const tempSphere = new THREE.Sphere()
    const tempIntersect = new THREE.Vector3()

    // 单结果快速路径：只追踪最近命中，避免收集+排序
    if (maxResults === 1) {
      let best: GpuPickResult | null = null
      let bestDepth = Infinity

      for (const splatInfo of splatInfos) {
        const resource = splatInfo.resource as IGSplatResource
        const centers = resource.centers
        if (!centers) {
          continue
        }

        const worldMatrix = splatInfo.node.matrixWorld

        const testSplat = (i: number) => {
          tempCenter.set(centers[i * 3], centers[i * 3 + 1], centers[i * 3 + 2])
          tempCenter.applyMatrix4(worldMatrix)

          const distToRay = ray.distanceToPoint(tempCenter)
          if (distToRay > sphereRadius) {
            return
          }

          tempSphere.set(tempCenter, sphereRadius)
          if (
            ray.intersectSphere(tempSphere, tempIntersect) &&
            (!accept || accept(splatInfo, i, tempCenter))
          ) {
            const depth = ray.origin.distanceTo(tempIntersect)
            if (depth < bestDepth) {
              bestDepth = depth
              best = {
                splatIndex: i,
                splatInfo,
                worldPosition: tempCenter.clone(),
                depth,
              }
            }
          }
        }

        // 仅遍历可见 splat（通过 intervals/activeSplats 过滤）
        const intervals = splatInfo.intervals
        if (intervals.length > 0) {
          for (let k = 0; k < intervals.length; k += 2) {
            for (let i = intervals[k]; i < intervals[k + 1]; i++) {
              testSplat(i)
            }
          }
        } else {
          const count = splatInfo.activeSplats > 0 ? splatInfo.activeSplats : splatInfo.numSplats
          for (let i = 0; i < count; i++) {
            testSplat(i)
          }
        }
      }

      return best ? [best] : []
    }

    // 多结果路径
    const results: GpuPickResult[] = []
    const insertResult = (
      splatInfo: GSplatInfo,
      splatIndex: number,
      worldPosition: THREE.Vector3,
      depth: number
    ) => {
      let low = 0
      let high = results.length
      while (low < high) {
        const middle = (low + high) >>> 1
        if ((results[middle]!.depth ?? Infinity) <= depth) {
          low = middle + 1
        } else {
          high = middle
        }
      }

      if (low >= maxResults) {
        return
      }

      results.splice(low, 0, {
        splatIndex,
        splatInfo,
        worldPosition: worldPosition.clone(),
        depth,
      })
      if (results.length > maxResults) {
        results.pop()
      }
    }

    for (const splatInfo of splatInfos) {
      const resource = splatInfo.resource as IGSplatResource
      const centers = resource.centers
      if (!centers) {
        continue
      }

      const worldMatrix = splatInfo.node.matrixWorld

      const testSplat = (i: number) => {
        tempCenter.set(centers[i * 3], centers[i * 3 + 1], centers[i * 3 + 2])
        tempCenter.applyMatrix4(worldMatrix)
        tempSphere.set(tempCenter, sphereRadius)

        if (
          ray.intersectSphere(tempSphere, tempIntersect) &&
          (!accept || accept(splatInfo, i, tempCenter))
        ) {
          insertResult(splatInfo, i, tempCenter, ray.origin.distanceTo(tempIntersect))
        }
      }

      // 仅遍历可见 splat
      const intervals = splatInfo.intervals
      if (intervals.length > 0) {
        for (let k = 0; k < intervals.length; k += 2) {
          for (let i = intervals[k]; i < intervals[k + 1]; i++) {
            testSplat(i)
          }
        }
      } else {
        const count = splatInfo.activeSplats > 0 ? splatInfo.activeSplats : splatInfo.numSplats
        for (let i = 0; i < count; i++) {
          testSplat(i)
        }
      }
    }

    return results
  }

  /**
   * 释放资源
   */
  dispose(): void {
    this.pickRenderTarget.dispose()
    this.idRanges.length = 0
    this.currentSplatInfos = []
    this.prepared = false
    this.renderCompleted = false
    this.hasPreparedCameraState = false
    this.preparedMinDistance = undefined

    // 清空临时场景
    this.clearPickScene()

    // 释放 Pick 材质
    for (const [, material] of this.pickMaterials) {
      material.dispose()
    }
    this.pickMaterials.clear()
  }

  /** 获取当前强引用资源的只读诊断快照。 */
  getDiagnostics(): Readonly<GSplatGpuPickerDiagnostics> {
    return {
      currentInfos: this.currentSplatInfos.length,
      pickMaterials: this.pickMaterials.size,
    }
  }

  /**
   * 全局帧计数器递增（在主循环中调用）
   *
   * 此方法应在每帧渲染开始时调用一次，用于追踪GPU异步渲染状态。
   * 配合isReady()方法使用，避免过早调用pick()导致GPU阻塞。
   */
  static incrementFrameId(): void {
    GSplatGpuPicker.currentFrameId++
  }
}
